import React from 'react';
import type { VoiceOrbState } from './LavaOrb';

export type { VoiceOrbState };

interface PlasmaOrbProps {
  state: VoiceOrbState;
  /** 0..1 mic / playback amplitude — pumps the glow when speech is detected. */
  amplitude?: number;
  /** Center text. Defaults are state-driven; pass to override (e.g. localization). */
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  className?: string;
  ariaLabel?: string;
}

const DEFAULT_LABELS: Record<VoiceOrbState, string> = {
  idle: 'You can talk',
  listening: 'You can talk',
  thinking: 'Thinking…',
  speaking: 'Speaking…',
};

/**
 * Paleta del orbe — Sistema 2.0. La animación (giro, pulso, remolino) vive en
 * index.css; aquí solo se sobreescriben las variables de color para sacar los
 * glows sky/violet/cyan y dejar navy + amarillo de marca. El estado sigue
 * distinguiéndose por tinte y por velocidad (esta última la fija el CSS).
 *
 * Valores en RGB porque el CSS los consume dentro de rgba(var(--…), α).
 */
const ORB_PALETTE: Record<VoiceOrbState, { g1: string; g2: string; g3: string }> = {
  // Escuchando / en reposo: amarillo de marca sobre navy — "te toca hablar".
  idle:      { g1: '250, 204, 21', g2: '35, 74, 135', g3: '252, 211, 77' },
  listening: { g1: '250, 204, 21', g2: '35, 74, 135', g3: '252, 211, 77' },
  // Pensando: marca más profunda (brand-600/700) — se lee "trabajando".
  thinking:  { g1: '202, 138, 4', g2: '26, 58, 107', g3: '161, 98, 7' },
  // Hablando: navy medio con filo de marca — claramente distinto de escuchar.
  speaking:  { g1: '80, 117, 181', g2: '250, 204, 21', g3: '155, 175, 212' },
};

/** navy-900 → navy-950, el mismo disco oscuro del sistema. */
const ORB_DISC = 'radial-gradient(circle at 50% 45%, #0F1D32 0%, #0B1629 80%)';

/**
 * Tech / sci-fi plasma orb — used by VoiceConversationModal as the visual
 * for the always-listening voice loop. State drives animation speed and glow
 * color; amplitude (0..1) intensifies the box-shadow in real time so it
 * reacts to your voice and the AI's playback.
 */
const PlasmaOrb: React.FC<PlasmaOrbProps> = ({
  state,
  amplitude,
  label,
  size = 'lg',
  onClick,
  className = '',
  ariaLabel,
}) => {
  const text = label ?? DEFAULT_LABELS[state] ?? '';
  const ampClamped = Math.min(1, Math.max(0, amplitude ?? 0));

  // CSS variables — index.css lee --plasma-amp para escalar los glows y
  // --plasma-glow-* para teñirlos; en línea ganan sobre las reglas por estado.
  const palette = ORB_PALETTE[state] ?? ORB_PALETTE.idle;
  const orbStyle = {
    ['--plasma-amp' as any]: ampClamped.toFixed(3),
    ['--plasma-glow-1' as any]: palette.g1,
    ['--plasma-glow-2' as any]: palette.g2,
    ['--plasma-glow-3' as any]: palette.g3,
    background: ORB_DISC,
  } as React.CSSProperties;

  const orb = (
    <div
      className={`plasma-orb plasma-orb--${size} plasma-orb--${state}`}
      style={orbStyle}
      aria-hidden={!onClick}
    >
      <div className="plasma-orb-filaments" />
      <div className="plasma-orb-core" />
      <div className="plasma-orb-label" style={{ color: '#FFFBEB', fontWeight: 600 }}>{text}</div>
    </div>
  );

  if (!onClick) {
    return (
      <div
        className={`inline-flex items-center justify-center ${className}`}
        role="img"
        aria-label={ariaLabel || `Voice assistant ${state}`}
      >
        {orb}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center bg-transparent border-0 p-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 rounded-full ${className}`}
      aria-label={ariaLabel || `Voice assistant ${state} — tap to exit`}
    >
      {orb}
    </button>
  );
};

export default PlasmaOrb;
